import { z } from 'zod';

import { USER_ROLES } from '../constants/index.js';

import { EmailSchema, IsoDateTimeSchema, UuidSchema } from './common.js';

export const UserRoleSchema = z
  .enum(USER_ROLES)
  .describe('Primary role assigned to a user account');
export type UserRole = z.infer<typeof UserRoleSchema>;

export const LoginRequestSchema = z.object({
  // Accepts EITHER the work email OR the user's CallerDesk number. Kept as
  // `email` for wire compatibility; validated as a loose identifier string
  // (email format is no longer enforced here since a phone number is valid).
  email: z
    .string()
    .trim()
    .min(3, 'Enter your email or CallerDesk number')
    .max(255)
    .describe('Login identifier: work email or CallerDesk number'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .describe('Plain-text password (transport secured via TLS, never logged)'),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const RefreshRequestSchema = z.object({
  refreshToken: z
    .string()
    .min(1)
    .describe('Opaque refresh token previously issued by /auth/login or /auth/refresh'),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

/**
 * Why a session ended. Sent by the client on /auth/logout so the audit trail
 * can tell a deliberate sign-out from an inactivity auto-logout. Defaults to
 * `manual` when omitted (any older client that posts an empty body).
 */
export const LogoutReasonSchema = z.enum(['manual', 'inactivity']);
export type LogoutReason = z.infer<typeof LogoutReasonSchema>;

export const LogoutRequestSchema = z.object({
  reason: LogoutReasonSchema.optional().describe('Why the session is ending (audit only)'),
});
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;

/**
 * Client-reported session-lifecycle events that only the browser can observe
 * (the inactivity warning appearing, the user extending). Posted to
 * /auth/session-events so the SERVER can stamp the real IP / time / geo into
 * the audit trail. Purely for the audit log - no behaviour depends on it.
 */
export const SessionEventTypeSchema = z.enum(['idle_warning', 'idle_extended']);
export type SessionEventType = z.infer<typeof SessionEventTypeSchema>;

export const SessionEventRequestSchema = z.object({
  type: SessionEventTypeSchema.describe('Which client-side session event occurred'),
});
export type SessionEventRequest = z.infer<typeof SessionEventRequestSchema>;

export const PasswordResetRequestSchema = z.object({
  email: EmailSchema.describe(
    'Account email to send a reset link to. Endpoint always returns 204 to avoid email enumeration.',
  ),
});

export const PasswordResetConfirmSchema = z
  .object({
    token: z.string().min(1).describe('Single-use reset token from the email link'),
    newPassword: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .describe('New password (min 12 chars). Stored as bcrypt hash, never plain text.'),
    confirmPassword: z
      .string()
      .describe('Must equal newPassword. Server returns 400 if they differ.'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const TokenPairSchema = z.object({
  accessToken: z.string().describe('Short-lived bearer JWT for the Authorization header'),
  refreshToken: z
    .string()
    .describe(
      'Longer-lived refresh JWT; rotate via /auth/refresh, store only in memory or HttpOnly cookie',
    ),
  expiresIn: z.number().int().positive().describe('Access-token lifetime in seconds'),
});
export type TokenPair = z.infer<typeof TokenPairSchema>;

export const AuthenticatedUserSchema = z.object({
  id: UuidSchema.describe('UUID v7 of the user'),
  email: EmailSchema.describe('Account email (case-insensitive, unique)'),
  fullName: z.string().describe('Display name for headers, mentions, audit logs'),
  role: UserRoleSchema,
  isActive: z.boolean().describe('Whether the user can sign in right now'),
  lastLogin: IsoDateTimeSchema.nullable().describe(
    'ISO timestamp of the last successful login, null if never signed in',
  ),
  /**
   * Codes of the roles assigned to this user in the Roles master (from
   * user_roles). For a custom-role user this is the custom role's code (e.g.
   * `marketing_in`); for a plain system role it is that role's code. Optional +
   * additive so existing token-built `request.user` objects stay valid. Used by
   * the admin UI to key role-specific behaviour (e.g. send marketing-only users
   * straight to the Marketing dashboard, skipping Welcome).
   */
  assignedRoleCodes: z.array(z.string()).optional(),
});
export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>;

export const LoginResponseSchema = z.object({
  user: AuthenticatedUserSchema,
  tokens: TokenPairSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
